// src/app/gen2ai/ui.service.ts
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, timer, from, of, firstValueFrom } from 'rxjs';
import { catchError, map, retry, switchMap, takeUntil, takeWhile } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// IMPORTANT: unify transcript types with googleaudio.service.ts
import type {
  LiveTranscriptChunk as GoogleLiveTranscriptChunk,
  LiveTranscriptRole as GoogleLiveTranscriptRole,
} from '../services/googleaudio.service';

export type LiveTranscriptChunk = GoogleLiveTranscriptChunk;
export type LiveTranscriptRole = GoogleLiveTranscriptRole;

// =====================================================================
// Public Interfaces & Types
// =====================================================================

export type UiVideoStatus = 'queued' | 'in_progress' | 'completed' | 'failed';

export interface UiVideoJobUpdate {
  id: string;
  status: UiVideoStatus;
  progress: number;
  videoUrl?: string | null;
  thumbnailUrl?: string | null;
  error?: string | null;
}

export type UiRtcConnectionState = 'connecting' | 'connected' | 'closed' | 'failed';

export interface UiRtcSession {
  close: () => void;
  muteMic: (muted: boolean) => void;
  isMicMuted$: Observable<boolean>;
  connectionState$: Observable<UiRtcConnectionState>;
}

export interface OpenAiRtcSession extends UiRtcSession {
  pc: RTCPeerConnection;
  dc: RTCDataChannel;
  localStream: MediaStream | null;
  remoteAudioEl: HTMLAudioElement;
  sendText: (text: string) => void;
  events$: Observable<unknown>;
}

export interface GrokRtcSession extends UiRtcSession {
  sendText: (text: string) => void;
  events$: Observable<unknown>;
}

export interface GoogleRtcSession extends UiRtcSession {
  sendText: (text: string) => void;
  transcript$: Observable<LiveTranscriptChunk>;
}

// =====================================================================
// API response shapes (for HTTP calls)
// =====================================================================

interface OpenAIVideoResponse {
  id?: string;
  status?: string;
  video_url?: string;
  url?: string;
  thumbnail_url?: string;
  progress?: number;
  error?: { message?: string };
}

interface GeminiLongRunningCreateResponse {
  name?: string;
}

interface GeminiOperationResponse {
  name?: string;
  done?: boolean;
  metadata?: { percentComplete?: number };
  error?: { message?: string };
  response?: {
    generateVideoResponse?: {
      generatedSamples?: Array<{
        video?: { uri?: string };
        videoUri?: string;
        thumbnailUri?: string;
      }>;
    };
  };
}

interface GrokVideoResponse {
  id?: string;
  video_url?: string;
  videoUrl?: string;
  url?: string;
  thumbnail_url?: string;
  thumbnailUrl?: string;
  status?: string;
  progress?: number;
  error?: string;
}

interface GrokSessionResponse {
  id?: string;
  session_id?: string;
  ws_url?: string;
  websocket_url?: string;
}

interface OpenAISessionResponse {
  client_secret?: { value?: string } | string;
}

// =====================================================================
// Service
// =====================================================================

@Injectable({ providedIn: 'root' })
export class UiService implements OnDestroy {
  // ── API Keys ───────────────────────────────────────────────────
  readonly geminiApiKey: string = String(environment.GEMINI_API_KEY || '').trim();
  readonly openAiApiKey: string = String(environment.OPENAI_API_KEY || '').trim();
  readonly grokApiKey: string = String(environment.GROK_API_KEY || '').trim();

  // ── Endpoints ─────────────────────────────────────────────────────
  readonly geminiBaseUrl = 'https://generativelanguage.googleapis.com/v1beta';
  readonly veoModelId = 'veo-3.1-generate-001';

  readonly grokVideoEndpoint = 'https://api.x.ai/v1/video/generations';

  readonly openAiRealtimeEndpoint = 'https://api.openai.com/v1/realtime/sessions';
  readonly grokAudioRtcEndpoint = 'https://api.x.ai/v1/realtime/sessions';

  readonly googleLiveApiEndpoint =
    'wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1beta.GenerativeService.BidiGenerateContent';

  // ── Models ────────────────────────────────────────────────────────
  readonly openAiRealtimeModel = 'gpt-4o-realtime-preview-2025-06-03';
  readonly openAiDefaultVoice = 'verse';

  readonly grokRealtimeModel = 'grok-3-fast';

  readonly geminiLiveModel = 'gemini-2.5-flash-preview-native-audio-dialog';

  // ── Teardown ──────────────────────────────────────────────────────
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly http: HttpClient) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // =================================================================
  //  VIDEO — Veo 3.1 (Google / Gemini)
  // =================================================================
  veoUIGenerate(opts: {
    prompt: string;
    seconds?: number;
    aspectRatio?: '16:9' | '9:16';
  }): Observable<UiVideoJobUpdate> {
    return this.veo31VideoGenUi(opts);
  }

  veo31VideoGenUi(opts: {
    prompt: string;
    seconds?: number;
    aspectRatio?: '16:9' | '9:16';
  }): Observable<UiVideoJobUpdate> {
    if (!this.geminiApiKey) {
      return of(this.failedJob('missing-key', 'Missing GEMINI_API_KEY for Veo 3.1 video.'));
    }

    const out$ = new Subject<UiVideoJobUpdate>();
    const url = `${this.geminiBaseUrl}/models/${this.veoModelId}:predictLongRunning?key=${this.geminiApiKey}`;

    const body = {
      instances: [{ prompt: opts.prompt }],
      parameters: {
        aspectRatio: opts.aspectRatio ?? '16:9',
        durationSeconds: opts.seconds ?? 8,
        resolution: '720p',
      },
    };

    this.http
      .post<GeminiLongRunningCreateResponse>(url, body, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      })
      .subscribe({
        next: (created) => {
          const operationName = created?.name;
          if (!operationName) {
            out$.next(this.failedJob('unknown', 'Missing operation name from Veo response.'));
            out$.complete();
            return;
          }

          out$.next({ id: operationName, status: 'queued', progress: 5 });

          let alive = true;

          timer(4000, 8000)
            .pipe(
              takeUntil(this.destroy$),
              takeWhile(() => alive),
              switchMap(() =>
                this.http
                  .get<GeminiOperationResponse>(`${this.geminiBaseUrl}/${operationName}?key=${this.geminiApiKey}`)
                  .pipe(
                    retry(2),
                    catchError((err) => {
                      alive = false;
                      out$.next(this.failedJob(operationName, this.extractError(err)));
                      out$.complete();
                      return of(null as any);
                    })
                  )
              )
            )
            .subscribe((op) => {
              if (!op || !alive) return;

              if (!op.done) {
                const pct = op.metadata?.percentComplete ?? 50;
                out$.next({
                  id: operationName,
                  status: 'in_progress',
                  progress: Math.min(pct, 95),
                });
                return;
              }

              alive = false;

              if (op.error) {
                out$.next(this.failedJob(operationName, op.error?.message || 'Veo generation failed.'));
                out$.complete();
                return;
              }

              const samples = op.response?.generateVideoResponse?.generatedSamples ?? [];
              const first = samples[0];
              const videoUrl = first?.video?.uri || first?.videoUri || null;
              const thumbnailUrl = first?.thumbnailUri || null;

              if (videoUrl) {
                out$.next(this.completedJob(operationName, videoUrl, thumbnailUrl));
              } else {
                out$.next(this.failedJob(operationName, 'No video returned from Veo.'));
              }
              out$.complete();
            });
        },
        error: (err) => {
          out$.next(this.failedJob('unknown', this.extractError(err)));
          out$.complete();
        },
      });

    return out$.asObservable();
  }

  // =================================================================
  //  VIDEO — Grok (xAI)
  // =================================================================
  grokVideoGenUi(opts: {
    prompt: string;
    seconds?: number;
    aspectRatio?: string;
  }): Observable<UiVideoJobUpdate> {
    if (!this.grokApiKey) {
      return of(this.failedJob('missing-key', 'Missing GROK_API_KEY for Grok video.'));
    }

    const payload = {
      model: 'grok-video-latest',
      prompt: opts.prompt,
      duration: opts.seconds ?? 8,
      aspect_ratio: opts.aspectRatio ?? '16:9',
    };

    const headers = this.bearerHeaders(this.grokApiKey);

    return this.http.post<GrokVideoResponse>(this.grokVideoEndpoint, payload, { headers }).pipe(
      map((resp) => {
        const id = resp?.id || `grok-video-${Date.now()}`;
        const videoUrl = resp?.video_url || resp?.videoUrl || resp?.url || null;
        return {
          id,
          status: (videoUrl ? 'completed' : (resp?.status || 'queued')) as UiVideoStatus,
          progress: videoUrl ? 100 : resp?.progress ?? 10,
          videoUrl,
          thumbnailUrl: resp?.thumbnail_url || resp?.thumbnailUrl || null,
          error: resp?.error || null,
        };
      }),
      catchError((err) => of(this.failedJob(`grok-video-${Date.now()}`, this.extractError(err))))
    );
  }

  // =================================================================
  //  RTC AUDIO — Google Gemini Live (WebSocket + PCM audio)
  // =================================================================
  async startGeminiAudioRtc(opts?: { systemInstruction?: string; voice?: string }): Promise<GoogleRtcSession> {
    return this.createGoogleLiveSession(opts);
  }

  async startGoogleAudioRtc(opts?: { systemInstruction?: string; voice?: string }): Promise<GoogleRtcSession> {
    return this.createGoogleLiveSession(opts);
  }

  async startClarichatVoiceRtc(opts?: { systemInstruction?: string }): Promise<GoogleRtcSession> {
    return this.createGoogleLiveSession({
      systemInstruction:
        opts?.systemInstruction ||
        'You are Clarichat, a helpful AI voice assistant. Be concise and conversational.',
      voice: 'Aoede',
    });
  }

  private async createGoogleLiveSession(opts?: { systemInstruction?: string; voice?: string }): Promise<GoogleRtcSession> {
    if (!this.geminiApiKey) {
      throw new Error('Missing GEMINI_API_KEY for Google Live audio.');
    }

    const transcript$ = new Subject<LiveTranscriptChunk>();
    const isMicMuted$ = new BehaviorSubject<boolean>(false);
    const connectionState$ = new BehaviorSubject<UiRtcConnectionState>('connecting');

    const wsUrl = `${this.googleLiveApiEndpoint}?key=${this.geminiApiKey}`;
    const ws = new WebSocket(wsUrl);

    await this.waitForWebSocket(ws, connectionState$);
    connectionState$.next('connected');

    const setupMessage: any = {
      setup: {
        model: `models/${this.geminiLiveModel}`,
        generationConfig: {
          responseModalities: ['AUDIO'],
          speechConfig: {
            voiceConfig: {
              prebuiltVoiceConfig: {
                voiceName: opts?.voice || 'Aoede',
              },
            },
          },
        },
      },
    };

    if (opts?.systemInstruction) {
      setupMessage.setup.systemInstruction = { parts: [{ text: opts.systemInstruction }] };
    }

    ws.send(JSON.stringify(setupMessage));

    // Wait for setupComplete
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Google Live setup timeout')), 10000);

      const handler = (event: MessageEvent) => {
        try {
          const data: any = JSON.parse(event.data);
          if (data?.setupComplete) {
            clearTimeout(timeout);
            ws.removeEventListener('message', handler);
            resolve();
          }
        } catch {
          // ignore
        }
      };

      ws.addEventListener('message', handler);
    });

    // Capture mic
    const localStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: 16000,
        channelCount: 1,
      },
    });

    const audioContext = new AudioContext({ sampleRate: 16000 });
    const source = audioContext.createMediaStreamSource(localStream);
    const processor = audioContext.createScriptProcessor(4096, 1, 1);

    source.connect(processor);
    processor.connect(audioContext.destination);

    processor.onaudioprocess = (e: any) => {
      if (isMicMuted$.value || ws.readyState !== WebSocket.OPEN) return;
      const inputData: Float32Array = e.inputBuffer.getChannelData(0);
      const pcm16 = this.float32ToPcm16(inputData);
      const base64Audio = this.arrayBufferToBase64(pcm16.buffer as ArrayBuffer);

      const msg = {
        realtimeInput: {
          mediaChunks: [
            {
              mimeType: 'audio/pcm;rate=16000',
              data: base64Audio,
            },
          ],
        },
      };

      try {
        ws.send(JSON.stringify(msg));
      } catch {
        // ignore
      }
    };

    // Playback
    const playbackContext = new AudioContext({ sampleRate: 24000 });
    const audioQueue: AudioBuffer[] = [];
    let isPlaying = false;

    const playNextBuffer = () => {
      if (audioQueue.length === 0) {
        isPlaying = false;
        return;
      }
      isPlaying = true;
      const buffer = audioQueue.shift()!;
      const bufferSource = playbackContext.createBufferSource();
      bufferSource.buffer = buffer;
      bufferSource.connect(playbackContext.destination);
      bufferSource.onended = playNextBuffer;
      bufferSource.start();
    };

    // Messages
    ws.addEventListener('message', (event: MessageEvent) => {
      try {
        const data: any = JSON.parse(event.data);

        const sc = data?.serverContent;
        if (!sc) return;

        const turnComplete = !!sc.turnComplete;

        // modelTurn parts: text and/or audio
        const parts = sc.modelTurn?.parts || [];
        for (const part of parts) {
          if (part?.text) {
            transcript$.next({ text: String(part.text), isFinal: turnComplete, role: 'model' });
          }

          const mime = part?.inlineData?.mimeType;
          const b64 = part?.inlineData?.data;
          if (mime?.startsWith('audio/') && b64) {
            const audioBytes = this.base64ToArrayBuffer(String(b64));
            const pcm16 = new Int16Array(audioBytes);
            const float32 = this.pcm16ToFloat32(pcm16);

            const audioBuffer = playbackContext.createBuffer(1, float32.length, 24000);
            audioBuffer.getChannelData(0).set(float32);

            audioQueue.push(audioBuffer);
            if (!isPlaying) playNextBuffer();
          }
        }

        // inputTranscript / outputTranscript
        if (sc.inputTranscript) {
          transcript$.next({ text: String(sc.inputTranscript), isFinal: true, role: 'user' });
        }
        if (sc.outputTranscript) {
          transcript$.next({ text: String(sc.outputTranscript), isFinal: true, role: 'model' });
        }
      } catch {
        // ignore
      }
    });

    ws.addEventListener('close', () => connectionState$.next('closed'));
    ws.addEventListener('error', () => connectionState$.next('failed'));

    const sendText = (text: string) => {
      if (ws.readyState !== WebSocket.OPEN) return;
      const msg = {
        clientContent: {
          turns: [{ role: 'user', parts: [{ text }] }],
          turnComplete: true,
        },
      };
      ws.send(JSON.stringify(msg));
    };

    const muteMic = (muted: boolean) => {
      isMicMuted$.next(muted);
      localStream.getAudioTracks().forEach((t) => (t.enabled = !muted));
    };

    const close = () => {
      try {
        processor.disconnect();
        source.disconnect();
      } catch {}
      try {
        audioContext.close();
      } catch {}
      try {
        playbackContext.close();
      } catch {}
      localStream.getTracks().forEach((t) => t.stop());
      try {
        ws.close();
      } catch {}

      transcript$.complete();
      connectionState$.next('closed');
      connectionState$.complete();
      isMicMuted$.complete();
    };

    return {
      close,
      muteMic,
      sendText,
      transcript$: transcript$.asObservable(),
      isMicMuted$: isMicMuted$.asObservable(),
      connectionState$: connectionState$.asObservable(),
    };
  }

  // =================================================================
  //  RTC AUDIO — Grok (xAI) WebSocket realtime
  // =================================================================
  async startGrokAudioRtc(opts?: { voice?: string; instructions?: string }): Promise<GrokRtcSession> {
    if (!this.grokApiKey) throw new Error('Missing GROK_API_KEY for Grok RTC audio.');

    const events$ = new Subject<unknown>();
    const isMicMuted$ = new BehaviorSubject<boolean>(false);
    const connectionState$ = new BehaviorSubject<UiRtcConnectionState>('connecting');

    const headers = this.bearerHeaders(this.grokApiKey);

    const sessionResp = await firstValueFrom(
      this.http.post<GrokSessionResponse>(
        this.grokAudioRtcEndpoint,
        {
          model: this.grokRealtimeModel,
          voice: opts?.voice || 'alloy',
          instructions: opts?.instructions || 'You are a helpful AI assistant. Be concise and conversational.',
          modalities: ['audio', 'text'],
          input_audio_format: 'pcm16',
          output_audio_format: 'pcm16',
        },
        { headers }
      )
    );

    const sessionId = sessionResp?.id || sessionResp?.session_id;
    const wsUrl =
      sessionResp?.ws_url ||
      sessionResp?.websocket_url ||
      `wss://api.x.ai/v1/realtime?session_id=${encodeURIComponent(String(sessionId || ''))}`;

    const ws = new WebSocket(wsUrl);
    await this.waitForWebSocket(ws, connectionState$);
    connectionState$.next('connected');

    const localStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: 24000,
        channelCount: 1,
      },
    });

    const audioContext = new AudioContext({ sampleRate: 24000 });
    const source = audioContext.createMediaStreamSource(localStream);
    const processor = audioContext.createScriptProcessor(4096, 1, 1);

    source.connect(processor);
    processor.connect(audioContext.destination);

    processor.onaudioprocess = (e: any) => {
      if (isMicMuted$.value || ws.readyState !== WebSocket.OPEN) return;

      const inputData: Float32Array = e.inputBuffer.getChannelData(0);
      const pcm16 = this.float32ToPcm16(inputData);
      const base64Audio = this.arrayBufferToBase64(pcm16.buffer as ArrayBuffer);

      try {
        ws.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: base64Audio }));
      } catch {}
    };

    const playbackContext = new AudioContext({ sampleRate: 24000 });
    const audioQueue: AudioBuffer[] = [];
    let isPlaying = false;

    const playNextBuffer = () => {
      if (audioQueue.length === 0) {
        isPlaying = false;
        return;
      }
      isPlaying = true;
      const buffer = audioQueue.shift()!;
      const bufferSource = playbackContext.createBufferSource();
      bufferSource.buffer = buffer;
      bufferSource.connect(playbackContext.destination);
      bufferSource.onended = playNextBuffer;
      bufferSource.start();
    };

    ws.addEventListener('message', (wsEvent: MessageEvent) => {
      try {
        const data: any = JSON.parse(wsEvent.data);
        events$.next(data);

        if (data?.type === 'response.audio.delta' && data?.delta) {
          const audioBytes = this.base64ToArrayBuffer(String(data.delta));
          const pcm16 = new Int16Array(audioBytes);
          const float32 = this.pcm16ToFloat32(pcm16);

          const audioBuffer = playbackContext.createBuffer(1, float32.length, 24000);
          audioBuffer.getChannelData(0).set(float32);

          audioQueue.push(audioBuffer);
          if (!isPlaying) playNextBuffer();
        }
      } catch {}
    });

    ws.addEventListener('close', () => connectionState$.next('closed'));
    ws.addEventListener('error', () => connectionState$.next('failed'));

    const sendText = (text: string) => {
      if (ws.readyState !== WebSocket.OPEN) return;
      ws.send(
        JSON.stringify({
          type: 'conversation.item.create',
          item: { type: 'message', role: 'user', content: [{ type: 'input_text', text }] },
        })
      );
      ws.send(JSON.stringify({ type: 'response.create' }));
    };

    const muteMic = (muted: boolean) => {
      isMicMuted$.next(muted);
      localStream.getAudioTracks().forEach((t) => (t.enabled = !muted));
    };

    const close = () => {
      try {
        processor.disconnect();
        source.disconnect();
      } catch {}
      try {
        audioContext.close();
      } catch {}
      try {
        playbackContext.close();
      } catch {}
      localStream.getTracks().forEach((t) => t.stop());
      try {
        ws.close();
      } catch {}

      events$.complete();
      connectionState$.next('closed');
      connectionState$.complete();
      isMicMuted$.complete();
    };

    return {
      close,
      muteMic,
      sendText,
      events$: events$.asObservable(),
      isMicMuted$: isMicMuted$.asObservable(),
      connectionState$: connectionState$.asObservable(),
    };
  }

  // =================================================================
  //  RTC AUDIO — OpenAI Realtime (WebRTC)
  // =================================================================
  startOpenAiRtcSession(opts?: { voice?: string; instructions?: string }): Observable<OpenAiRtcSession> {
    return from(this.createOpenAiRtcSession(opts));
  }

  private async createOpenAiRtcSession(opts?: { voice?: string; instructions?: string }): Promise<OpenAiRtcSession> {
    if (!this.openAiApiKey) throw new Error('Missing OPENAI_API_KEY for OpenAI RTC.');

    const events$ = new Subject<unknown>();
    const isMicMuted$ = new BehaviorSubject<boolean>(false);
    const connectionState$ = new BehaviorSubject<UiRtcConnectionState>('connecting');

    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.openAiApiKey}`,
      'Content-Type': 'application/json',
    });

    const sessionResp = await firstValueFrom(
      this.http.post<OpenAISessionResponse>(
        this.openAiRealtimeEndpoint,
        {
          model: this.openAiRealtimeModel,
          voice: opts?.voice || this.openAiDefaultVoice,
          instructions: opts?.instructions || 'You are a helpful AI assistant. Respond naturally in conversation.',
          modalities: ['audio', 'text'],
          input_audio_format: 'pcm16',
          output_audio_format: 'pcm16',
          turn_detection: {
            type: 'server_vad',
            threshold: 0.5,
            prefix_padding_ms: 300,
            silence_duration_ms: 500,
          },
          input_audio_transcription: { model: 'gpt-4o-mini-transcribe' },
        } as any,
        { headers }
      )
    );

    const clientSecret =
      (typeof sessionResp?.client_secret === 'object' && sessionResp?.client_secret?.value) ||
      (typeof sessionResp?.client_secret === 'string' ? sessionResp.client_secret : undefined);

    if (!clientSecret) throw new Error('OpenAI RTC session did not return a client_secret.');

    const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });

    pc.addEventListener('connectionstatechange', () => {
      const state = pc.connectionState;
      if (state === 'connected') connectionState$.next('connected');
      else if (state === 'closed' || state === 'disconnected') connectionState$.next('closed');
      else if (state === 'failed') connectionState$.next('failed');
    });

    const remoteAudioEl = document.createElement('audio');
    remoteAudioEl.autoplay = true;

    pc.ontrack = (e: RTCTrackEvent) => {
      const [stream] = e.streams;
      if (stream) remoteAudioEl.srcObject = stream;
    };

    const localStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
        sampleRate: 24000,
      },
    });

    for (const track of localStream.getTracks()) pc.addTrack(track, localStream);

    const dc = pc.createDataChannel('oai-events');

    dc.addEventListener('open', () => connectionState$.next('connected'));
    dc.addEventListener('close', () => connectionState$.next('closed'));
    dc.addEventListener('message', (e: MessageEvent) => {
      try {
        events$.next(JSON.parse(e.data));
      } catch {}
    });

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const sdpResp = await fetch(`https://api.openai.com/v1/realtime?model=${this.openAiRealtimeModel}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${clientSecret}`,
        'Content-Type': 'application/sdp',
        'OpenAI-Beta': 'realtime=v1',
      },
      body: offer.sdp || '',
    });

    if (!sdpResp.ok) {
      const errorText = await sdpResp.text();
      try {
        dc.close();
      } catch {}
      try {
        pc.close();
      } catch {}
      localStream.getTracks().forEach((t) => t.stop());
      throw new Error(`OpenAI SDP exchange failed: ${sdpResp.status} - ${errorText}`);
    }

    const answerSdp = await sdpResp.text();
    await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

    const sendText = (text: string) => {
      if (dc.readyState !== 'open') return;

      dc.send(
        JSON.stringify({
          type: 'conversation.item.create',
          item: { type: 'message', role: 'user', content: [{ type: 'input_text', text }] },
        })
      );

      dc.send(JSON.stringify({ type: 'response.create', response: { modalities: ['audio', 'text'] } }));
    };

    const muteMic = (muted: boolean) => {
      isMicMuted$.next(muted);
      localStream.getAudioTracks().forEach((track) => (track.enabled = !muted));
    };

    const close = () => {
      try {
        dc.close();
      } catch {}
      try {
        pc.close();
      } catch {}
      localStream.getTracks().forEach((t) => t.stop());
      remoteAudioEl.srcObject = null;

      events$.complete();
      connectionState$.next('closed');
      connectionState$.complete();
      isMicMuted$.complete();
    };

    return {
      pc,
      dc,
      localStream,
      remoteAudioEl,
      sendText,
      muteMic,
      close,
      events$: events$.asObservable(),
      isMicMuted$: isMicMuted$.asObservable(),
      connectionState$: connectionState$.asObservable(),
    };
  }

  // =================================================================
  //  Shared WebSocket helper
  // =================================================================
  private waitForWebSocket(ws: WebSocket, connectionState$: BehaviorSubject<UiRtcConnectionState>): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onError);
        connectionState$.next('failed');
        reject(new Error('WebSocket connection timed out.'));
      }, 15000);

      const onOpen = () => {
        clearTimeout(timeout);
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onError);
        resolve();
      };

      const onError = () => {
        clearTimeout(timeout);
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onError);
        connectionState$.next('failed');
        reject(new Error('WebSocket connection failed.'));
      };

      ws.addEventListener('open', onOpen);
      ws.addEventListener('error', onError);
    });
  }

  // =================================================================
  //  Audio conversion utilities
  // =================================================================
  private float32ToPcm16(float32: Float32Array): Int16Array {
    const pcm16 = new Int16Array(float32.length);
    for (let i = 0; i < float32.length; i++) {
      const s = Math.max(-1, Math.min(1, float32[i]));
      pcm16[i] = s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7fff);
    }
    return pcm16;
  }

  private pcm16ToFloat32(pcm16: Int16Array): Float32Array {
    const float32 = new Float32Array(pcm16.length);
    for (let i = 0; i < pcm16.length; i++) float32[i] = pcm16[i] / 32768;
    return float32;
  }

  // =================================================================
  //  General utility helpers
  // =================================================================
  private bearerHeaders(apiKey: string): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
    });
  }

  private failedJob(id: string, error: string): UiVideoJobUpdate {
    return { id, status: 'failed', progress: 0, error };
  }

  private completedJob(id: string, videoUrl: string | null, thumbnailUrl?: string | null): UiVideoJobUpdate {
    return { id, status: 'completed', progress: 100, videoUrl, thumbnailUrl: thumbnailUrl || null };
  }

  private extractError(err: any): string {
    if (typeof err === 'string') return err;
    return err?.error?.error?.message || err?.error?.message || err?.message || err?.statusText || 'Unknown error';
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const chunkSize = 8192;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
      for (let j = 0; j < chunk.length; j++) binary += String.fromCharCode(chunk[j]);
    }
    return btoa(binary);
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
    return bytes.buffer;
  }
}
